import React, { useRef, useState } from 'react';
import { TEST_DIFFICULTY_LABEL } from '../../../constants/testDifficulty';
import type { TestDifficulty } from '../../../types';
import QuestionInlineEditor from '../../../components/QuestionInlineEditor';
import type { EditableQuestion } from '../../../components/QuestionInlineEditor';
import {
  errorDetail, fetchTestQuestions, humanError, removeQuestion, reorderQuestions, restoreQuestion,
} from '../../api';
import { ConfirmPanel } from '../../components/ConfirmPanel';
import { Icon } from '../../components/icons';
import { QuestionRow } from '../../components/QuestionRow';
import { ORDER_HINT } from '../../model';
import type { OverviewDetail } from '../../model';
import type { WorkspaceNotice } from '../../notices';
import { questionTypeLabel, testsIn } from '../../words';

export interface QuestionsTabProps {
  detail: OverviewDetail;
  reload: () => Promise<void>;
  notify: (notice: WorkspaceNotice) => void;
  onAdd: () => void;
  onCopyTest: () => void;
}

/** Вопроса уже нет в тесте (убран в другом окне): сервер ответил 404 или вопроса нет в свежем списке. */
const QUESTION_GONE = 'Этого вопроса уже нет в тесте. Список обновлён.';

export const QuestionsTab: React.FC<QuestionsTabProps> = ({ detail, reload, notify, onAdd, onCopyTest }) => {
  const [editing, setEditing] = useState<EditableQuestion | null>(null);
  const [recentlyRemovedId, setRecentlyRemovedId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [movedId, setMovedId] = useState<number | null>(null);
  const [moving, setMoving] = useState(false);
  const [removing, setRemoving] = useState(false);
  // ref, а не состояние: два нажатия до перерисовки видят прежние moving/removing = false; состояния нужны только для `disabled`.
  // Перемещение и удаление блокируют друг друга: «Ниже» во время `DELETE` отправил бы устаревший полный список (`question_order_mismatch`).
  const movingRef = useRef(false);
  const removingRef = useRef(false);
  const openingRef = useRef(false); // «Изменить»: второй медленный ответ сбросил бы текст, который успели набрать в окне редактора

  const archived = detail.status === 'archived';
  const questions = detail.questions;

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (movingRef.current || removingRef.current || target < 0 || target >= questions.length) return;
    const ids = questions.map((question) => question.id);
    const held = ids[index];
    ids[index] = ids[target];
    ids[target] = held;
    movingRef.current = true;
    setMoving(true);
    try {
      await reorderQuestions(detail.id, ids);
      setMovedId(held);
      await reload();
    } catch (error) {
      const info = errorDetail(error);
      notify({
        kind: 'info',
        text: info?.code === 'question_order_mismatch'
          ? 'Список вопросов изменился, мы его обновили. Попробуйте ещё раз.'
          : humanError(error, 'Не получилось поменять порядок. Попробуйте ещё раз.'),
      });
      await reload();
    } finally {
      movingRef.current = false;
      setMoving(false);
    }
  };

  const startEdit = async (questionId: number) => {
    if (openingRef.current) return;
    openingRef.current = true;
    try {
      const list = await fetchTestQuestions(detail.id);
      const found = list.find((question) => question.id === questionId);
      if (found) {
        setEditing(found);
      } else {
        notify({ kind: 'info', text: QUESTION_GONE });
        await reload();
      }
    } catch (error) {
      notify({ kind: 'info', text: humanError(error, 'Не получилось открыть вопрос. Попробуйте ещё раз.') });
    } finally {
      openingRef.current = false;
    }
  };

  const confirmRemove = async (questionId: number) => {
    if (removingRef.current || movingRef.current) return; // одновременно идёт только одно изменение состава или порядка
    removingRef.current = true;
    setRemoving(true);
    // Ответ приходит позже нажатия: подтверждение, открытое за это время у другой строки, закрывать нельзя.
    try {
      await removeQuestion(questionId);
      setRecentlyRemovedId(questionId);
      setRemovingId((current) => (current === questionId ? null : current));
      setMovedId(null);
      notify({ kind: 'ok', text: 'Вопрос убран из теста.' });
      await reload();
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 404) {
        setRemovingId((current) => (current === questionId ? null : current));
        notify({ kind: 'info', text: QUESTION_GONE });
        await reload();
      } else {
        notify({ kind: 'info', text: humanError(error, 'Не получилось убрать вопрос. Попробуйте ещё раз.') });
      }
    } finally {
      removingRef.current = false;
      setRemoving(false);
    }
  };

  const undoRemove = async () => {
    if (recentlyRemovedId === null || removingRef.current || movingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    try {
      await restoreQuestion(recentlyRemovedId);
      setRecentlyRemovedId(null);
      notify({ kind: 'ok', text: 'Вопрос возвращён в тест. Результаты пересчитаны.' });
      await reload();
    } catch (error) {
      notify({ kind: 'info', text: humanError(error, 'Не получилось вернуть вопрос. Попробуйте ещё раз.') });
    } finally {
      removingRef.current = false;
      setRemoving(false);
    }
  };

  const completed = detail.attempts.completed;
  return (
    <div className="tw-card tw-stack-sm">
      <div className="tw-row tw-between">
        <h2>{`Вопросы в тесте: ${questions.length}`}</h2>
        {!archived && (
          <button type="button" className="tw-btn" onClick={onAdd}>
            <Icon name="plus" />
            Добавить вопросы
          </button>
        )}
      </div>
      {questions.length >= 2 && !archived && <p className="tw-help">{ORDER_HINT}</p>}
      {completed > 0 && !archived && (
        <div className="tw-warn">
          <Icon name="info" />
          <span>{`Тест уже проходили: ${completed}. После удаления вопроса результаты пересчитываются по оставшимся вопросам. История ответов сохраняется.`}</span>
        </div>
      )}
      {recentlyRemovedId !== null && !archived && (
        <div className="tw-row">
          <span className="tw-help">Вопрос убран, история ответов сохранена.</span>
          <button type="button" className="tw-btn" disabled={removing || moving} onClick={() => void undoRemove()}>
            Вернуть вопрос
          </button>
        </div>
      )}
      <ul className="tw-qlist">
        {questions.map((question, index) => {
          const note = question.shared_in_tests > 0
            ? 'В других тестах этот вопрос останется.'
            : 'Этот вопрос есть только в этом тесте. Он будет убран из текущего состава; история ответов сохранится.';
          return (
            <li key={question.id} className="tw-qitem">
              <QuestionRow
                number={index + 1}
                text={question.text}
                meta={`${questionTypeLabel(question.type)} · Сложность: ${question.difficulty ? TEST_DIFFICULTY_LABEL[question.difficulty as TestDifficulty] : 'не задана'}${question.shared_in_tests > 0 ? ` · Есть ещё в ${testsIn(question.shared_in_tests)}` : ''}`}
                moved={movedId === question.id}
                canUp={index > 0 && !moving && !removing}
                canDown={index < questions.length - 1 && !moving && !removing}
                onUp={() => void move(index, -1)}
                onDown={() => void move(index, 1)}
                onEdit={() => void startEdit(question.id)}
                onRemove={() => setRemovingId(question.id)}
                removeLabel="Убрать"
                readOnly={archived}
              />
              {removingId === question.id && !archived && (
                <ConfirmPanel
                  title="Убрать этот вопрос из теста?"
                  text={completed > 0
                    ? `${note} Тест уже проходили: ${completed}. Вопрос и ответы сотрудников останутся в истории. Результаты будут пересчитаны по оставшимся вопросам.`
                    : note}
                  confirmLabel="Да, убрать"
                  cancelLabel="Нет, оставить"
                  busy={removing || moving}
                  onConfirm={() => void confirmRemove(question.id)}
                  onCancel={() => setRemovingId(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
      <QuestionInlineEditor
        question={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          setMovedId(null);
          notify({ kind: 'ok', text: 'Вопрос изменён.' });
          void reload();
        }}
      />
    </div>
  );
};
